import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, AlertTriangle, CheckCircle, Search } from 'lucide-react';
import { MetaIcon } from 'components';
import { Facebook, Instagram } from 'lucide-react';

const MetaDropdowns = ({
  accounts,
  selectedAccountId,
  selectedFacebookPageId,
  selectedInstagramId,
  selectedWhatsappPhone,
  onAccountSelect,
  onFacebookPageSelect,
  onInstagramSelect,
  onWhatsappSelect,
  disabled = false,
  showOnlyAccount = false,
  showFacebookPage = true,
  showInstagram = true,
  showWhatsapp = true,
  facebookPageRequired = false,
  instagramRequired = false,
  whatsappRequired = false,
  lockAccountSelection = false,
}) => {
  // Dropdown states
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [isFacebookDropdownOpen, setIsFacebookDropdownOpen] = useState(false);
  const [isInstagramDropdownOpen, setIsInstagramDropdownOpen] = useState(false);
  const [isWhatsappDropdownOpen, setIsWhatsappDropdownOpen] = useState(false);

  // Refs for positioning and outside click
  const accountDropdownBtnRef = useRef();
  const accountDropdownContentRef = useRef();
  const facebookDropdownBtnRef = useRef();
  const instagramDropdownBtnRef = useRef();
  const whatsappDropdownBtnRef = useRef();

  // Positions for dropdowns
  const [accountDropdownPos, setAccountDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [facebookDropdownPos, setFacebookDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [instagramDropdownPos, setInstagramDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [whatsappDropdownPos, setWhatsappDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Helper data
  const selectedAccount = accounts.find(acc => acc.account_id === selectedAccountId);
  const facebookPages = selectedAccount?.pages || [];
  const instagramAccounts = selectedAccount?.instagram_accounts || [];
  const whatsappAccounts = selectedAccount?.whatsapp_business_accounts || [];
  const whatsappPhones = whatsappAccounts.flatMap(acc => acc.phone_numbers || []);
  const shouldShowFacebookPage = !showOnlyAccount && showFacebookPage;
  const shouldShowInstagram = !showOnlyAccount && showInstagram;
  const shouldShowWhatsapp = !showOnlyAccount && showWhatsapp;
  const isAccountInteractionDisabled = disabled || lockAccountSelection;

  // Update dropdown positions
  useEffect(() => {
    if (isAccountDropdownOpen && accountDropdownBtnRef.current) {
      const updatePosition = () => {
        if (accountDropdownBtnRef.current) {
          const rect = accountDropdownBtnRef.current.getBoundingClientRect();
          setAccountDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };

      const debouncedUpdate = () => {
        requestAnimationFrame(updatePosition);
      };

      updatePosition();

      // Listen for scroll on all parent containers
      const scrollContainers = [];
      let parent = accountDropdownBtnRef.current.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          parent.addEventListener('scroll', debouncedUpdate, { passive: true });
          scrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }

      // Global scroll and resize
      window.addEventListener('scroll', debouncedUpdate, { passive: true });
      window.addEventListener('resize', debouncedUpdate, { passive: true });

      // ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (accountDropdownBtnRef.current) {
        resizeObserver.observe(accountDropdownBtnRef.current);
      }

      return () => {
        scrollContainers.forEach(container => {
          container.removeEventListener('scroll', debouncedUpdate);
        });
        window.removeEventListener('scroll', debouncedUpdate);
        window.removeEventListener('resize', debouncedUpdate);
        resizeObserver.disconnect();
      };
    }
  }, [isAccountDropdownOpen]);

  useEffect(() => {
    if (isFacebookDropdownOpen && facebookDropdownBtnRef.current) {
      const updatePosition = () => {
        if (facebookDropdownBtnRef.current) {
          const rect = facebookDropdownBtnRef.current.getBoundingClientRect();
          setFacebookDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };

      const debouncedUpdate = () => {
        requestAnimationFrame(updatePosition);
      };

      updatePosition();

      // Listen for scroll on all parent containers
      const scrollContainers = [];
      let parent = facebookDropdownBtnRef.current.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          parent.addEventListener('scroll', debouncedUpdate, { passive: true });
          scrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }

      // Global scroll and resize
      window.addEventListener('scroll', debouncedUpdate, { passive: true });
      window.addEventListener('resize', debouncedUpdate, { passive: true });

      // ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (facebookDropdownBtnRef.current) {
        resizeObserver.observe(facebookDropdownBtnRef.current);
      }

      return () => {
        scrollContainers.forEach(container => {
          container.removeEventListener('scroll', debouncedUpdate);
        });
        window.removeEventListener('scroll', debouncedUpdate);
        window.removeEventListener('resize', debouncedUpdate);
        resizeObserver.disconnect();
      };
    }
  }, [isFacebookDropdownOpen]);

  useEffect(() => {
    if (isInstagramDropdownOpen && instagramDropdownBtnRef.current) {
      const updatePosition = () => {
        if (instagramDropdownBtnRef.current) {
          const rect = instagramDropdownBtnRef.current.getBoundingClientRect();
          setInstagramDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };

      const debouncedUpdate = () => {
        requestAnimationFrame(updatePosition);
      };

      updatePosition();

      // Listen for scroll on all parent containers
      const scrollContainers = [];
      let parent = instagramDropdownBtnRef.current.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          parent.addEventListener('scroll', debouncedUpdate, { passive: true });
          scrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }

      // Global scroll and resize
      window.addEventListener('scroll', debouncedUpdate, { passive: true });
      window.addEventListener('resize', debouncedUpdate, { passive: true });

      // ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (instagramDropdownBtnRef.current) {
        resizeObserver.observe(instagramDropdownBtnRef.current);
      }

      return () => {
        scrollContainers.forEach(container => {
          container.removeEventListener('scroll', debouncedUpdate);
        });
        window.removeEventListener('scroll', debouncedUpdate);
        window.removeEventListener('resize', debouncedUpdate);
        resizeObserver.disconnect();
      };
    }
  }, [isInstagramDropdownOpen]);

  useEffect(() => {
    if (isWhatsappDropdownOpen && whatsappDropdownBtnRef.current) {
      const updatePosition = () => {
        if (whatsappDropdownBtnRef.current) {
          const rect = whatsappDropdownBtnRef.current.getBoundingClientRect();
          setWhatsappDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };

      const debouncedUpdate = () => {
        requestAnimationFrame(updatePosition);
      };

      updatePosition();

      // Listen for scroll on all parent containers
      const scrollContainers = [];
      let parent = whatsappDropdownBtnRef.current.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          parent.addEventListener('scroll', debouncedUpdate, { passive: true });
          scrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }

      // Global scroll and resize
      window.addEventListener('scroll', debouncedUpdate, { passive: true });
      window.addEventListener('resize', debouncedUpdate, { passive: true });

      // ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (whatsappDropdownBtnRef.current) {
        resizeObserver.observe(whatsappDropdownBtnRef.current);
      }

      return () => {
        scrollContainers.forEach(container => {
          container.removeEventListener('scroll', debouncedUpdate);
        });
        window.removeEventListener('scroll', debouncedUpdate);
        window.removeEventListener('resize', debouncedUpdate);
        resizeObserver.disconnect();
      };
    }
  }, [isWhatsappDropdownOpen]);

  // Reset account search when dropdown closes
  useEffect(() => {
    if (!isAccountDropdownOpen) setAccountSearchQuery('');
  }, [isAccountDropdownOpen]);

  useEffect(() => {
    if (lockAccountSelection && isAccountDropdownOpen) {
      setIsAccountDropdownOpen(false);
    }
  }, [lockAccountSelection, isAccountDropdownOpen]);

  // Filter accounts by search (name, account_id)
  const filteredAccounts = accountSearchQuery.trim()
    ? accounts.filter(acc => {
        const q = accountSearchQuery.toLowerCase().trim();
        return (acc.name || '').toLowerCase().includes(q) ||
               (acc.account_id || '').toLowerCase().includes(q);
      })
    : accounts;

  // Close other dropdowns when one opens
  const handleOpenDropdown = (dropdown) => {
    if (dropdown === 'account') {
      setIsAccountDropdownOpen(!isAccountDropdownOpen);
      setAccountSearchQuery('');
      setIsFacebookDropdownOpen(false);
      setIsInstagramDropdownOpen(false);
      setIsWhatsappDropdownOpen(false);
    } else if (dropdown === 'facebook') {
      setIsAccountDropdownOpen(false);
      setIsFacebookDropdownOpen(!isFacebookDropdownOpen);
      setIsInstagramDropdownOpen(false);
      setIsWhatsappDropdownOpen(false);
    } else if (dropdown === 'instagram') {
      setIsAccountDropdownOpen(false);
      setIsFacebookDropdownOpen(false);
      setIsInstagramDropdownOpen(!isInstagramDropdownOpen);
      setIsWhatsappDropdownOpen(false);
    } else if (dropdown === 'whatsapp') {
      setIsAccountDropdownOpen(false);
      setIsFacebookDropdownOpen(false);
      setIsInstagramDropdownOpen(false);
      setIsWhatsappDropdownOpen(!isWhatsappDropdownOpen);
    }
  };

  // Outside click handlers
  useEffect(() => {
    if (!isAccountDropdownOpen) return;
    const handleClick = (e) => {
      const insideButton = accountDropdownBtnRef.current?.contains(e.target);
      const insideDropdown = accountDropdownContentRef.current?.contains(e.target);
      if (!insideButton && !insideDropdown) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isAccountDropdownOpen]);

  useEffect(() => {
    if (!isFacebookDropdownOpen) return;
    const handleClick = (e) => {
      if (facebookDropdownBtnRef.current && !facebookDropdownBtnRef.current.contains(e.target)) {
        setIsFacebookDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isFacebookDropdownOpen]);

  useEffect(() => {
    if (!isInstagramDropdownOpen) return;
    const handleClick = (e) => {
      if (instagramDropdownBtnRef.current && !instagramDropdownBtnRef.current.contains(e.target)) {
        setIsInstagramDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isInstagramDropdownOpen]);

  useEffect(() => {
    if (!isWhatsappDropdownOpen) return;
    const handleClick = (e) => {
      if (whatsappDropdownBtnRef.current && !whatsappDropdownBtnRef.current.contains(e.target)) {
        setIsWhatsappDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isWhatsappDropdownOpen]);

  const isAccountDisabled = (account) => {
    return account.status !== 'ACTIVE' && account.status !== 'UNSETTLED';
  };

  const getAccountStatusInfo = (account) => {
    if (account.status === "UNSETTLED") {
      return {
        type: 'warning',
        icon: AlertTriangle,
        text: 'Pago pendiente',
        description: 'Esta cuenta tiene un pago pendiente. Verifica el estado en Facebook Ads Manager.',
      };
    }
    if (account.status !== 'ACTIVE') {
      return {
        type: 'error',
        icon: AlertTriangle,
        text: 'Inactiva',
        description: 'Esta cuenta no está activa. Verifica el estado en Facebook Ads Manager.',
      };
    }
    return {
      type: 'success',
      icon: CheckCircle,
      text: 'Activa',
      description: 'Esta cuenta está activa y lista para usar.',
    };
  };

  return (
    <div className="space-y-3">
      {/* Account Dropdown */}
      <div>
        <label className="block text-xs font-medium text-white/90 mb-2">
          Cuenta de Meta Ads <span className="text-red-400">*</span>
          {lockAccountSelection && (
            <span className="text-white/60 text-xs ml-1">(seleccionada en Conexiones)</span>
          )}
        </label>
        <div className="relative">
          <button
            type="button"
            ref={accountDropdownBtnRef}
            onClick={() => !isAccountInteractionDisabled && handleOpenDropdown('account')}
            disabled={isAccountInteractionDisabled}
            className={`w-full px-3 py-2 text-left border-2 rounded-lg shadow-sm flex items-center justify-between text-sm backdrop-blur-md transition-all ${
              !isAccountInteractionDisabled
                ? 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 text-white'
                : lockAccountSelection
                  ? 'bg-white/10 border-white/30 cursor-not-allowed text-white'
                  : 'bg-white/5 border-white/20 cursor-not-allowed text-white/50'
            }`}
          >
            <div className="flex items-center">
              {selectedAccountId ? (
                <div className="font-medium text-white text-sm">{selectedAccount?.name}</div>
              ) : (
                <span className="text-white/60 text-sm">Seleccionar cuenta</span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''} ${lockAccountSelection ? 'opacity-40' : ''}`} />
          </button>

          {isAccountDropdownOpen && createPortal(
            <div
              ref={accountDropdownContentRef}
              className="z-[11000] backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-2xl overflow-hidden"
              style={{
                position: 'fixed',
                top: accountDropdownPos.top,
                left: accountDropdownPos.left,
                width: accountDropdownPos.width,
                minWidth: 240,
                padding: 0,
              }}
            >
              <div className="p-2 border-b border-white/20 sticky top-0 bg-white/10 backdrop-blur-sm">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                  <input
                    type="text"
                    value={accountSearchQuery}
                    onChange={(e) => setAccountSearchQuery(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    placeholder="Buscar cuenta por nombre o ID..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-auto py-2">
                {filteredAccounts.length === 0 ? (
                  <div className="px-4 py-6 text-center text-white/60 text-sm">No se encontraron cuentas</div>
                ) : (
                  filteredAccounts.map((account) => {
                    const isDisabled = isAccountDisabled(account);
                    const statusInfo = getAccountStatusInfo(account);
                    const StatusIcon = statusInfo.icon;
                    const statusPill =
                      statusInfo.type === 'success'
                        ? 'bg-green-500/20 text-green-300 border-green-400/30'
                        : statusInfo.type === 'warning'
                          ? 'bg-orange-500/20 text-orange-300 border-orange-400/30'
                          : 'bg-red-500/20 text-red-300 border-red-400/30';

                    return (
                      <button
                        key={account.account_id}
                        onMouseDown={(e) => {
                          if (isDisabled) return;
                          e.stopPropagation();
                          onAccountSelect?.(account);
                          setIsAccountDropdownOpen(false);
                        }}
                        disabled={isDisabled}
                        className={`w-full px-4 py-2 text-left flex items-center border-b border-white/10 last:border-b-0 transition-all ${
                          isDisabled
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:bg-white/20 focus:bg-white/20 focus:outline-none'
                        } ${account.account_id === selectedAccountId ? 'bg-blue-500/30 font-semibold' : ''}`}
                        title={statusInfo.description}
                      >
                        <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20">
                          <MetaIcon width={16} height={16} className="text-blue-300" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-white flex items-center gap-2">
                            {account.name}
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium backdrop-blur-md border ${statusPill}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.text}
                            </span>
                          </div>
                          <div className="text-sm text-white/70">{account.account_id}</div>
                          {isDisabled && <div className="text-xs text-white/50 mt-1">{statusInfo.description}</div>}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Facebook Page Dropdown */}
      {shouldShowFacebookPage && (
        <div>
          <label className="block text-xs font-medium text-white/90 mb-2">
            Página de Facebook{' '}
            {facebookPageRequired ? (
              <span className="text-red-400">*</span>
            ) : (
              <span className="text-white/60 text-xs">(opcional)</span>
            )}
          </label>
          <div className="relative">
            <button
              type="button"
              ref={facebookDropdownBtnRef}
              onClick={() => selectedAccountId && facebookPages.length && !disabled && handleOpenDropdown('facebook')}
              disabled={!selectedAccountId || !facebookPages.length || disabled}
              className={`w-full px-3 py-2 text-left border-2 rounded-lg shadow-sm flex items-center justify-between text-sm backdrop-blur-md transition-all ${
                selectedAccountId && facebookPages.length && !disabled
                  ? 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 text-white'
                  : 'bg-white/5 border-white/20 cursor-not-allowed text-white/50'
              }`}
            >
              <div className="flex items-center">
                {selectedFacebookPageId ? (
                  <div className="font-medium text-white text-sm">{facebookPages.find(page => page.id === selectedFacebookPageId)?.name}</div>
                ) : (
                  <span className="text-white/60 text-sm">
                    {!selectedAccountId ? "Selecciona una cuenta primero" : !facebookPages.length ? "No hay páginas disponibles" : "Seleccionar página"}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isFacebookDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isFacebookDropdownOpen && facebookPages.length > 0 && createPortal(
              <div
                className="z-[11000] backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-2xl max-h-72 overflow-auto py-2"
                style={{
                  position: 'fixed',
                  top: facebookDropdownPos.top,
                  left: facebookDropdownPos.left,
                  width: facebookDropdownPos.width,
                  minWidth: 240,
                  padding: 0,
                }}
              >
                {facebookPages.map((page) => (
                  <button
                    key={page.id}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      onFacebookPageSelect?.(page);
                      setIsFacebookDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left flex items-center border-b border-white/10 last:border-b-0 hover:bg-white/20 focus:bg-white/20 focus:outline-none transition-all ${page.id === selectedFacebookPageId ? 'bg-blue-500/30 font-semibold' : ''}`}
                  >
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20">
                      <Facebook className="w-4 h-4 text-blue-300" />
                    </div>
                    <div>
                      <div className="font-medium text-white">{page.name}</div>
                      <div className="text-sm text-white/70">{page.fan_count} seguidores</div>
                    </div>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>
      )}

      {/* Instagram Dropdown */}
      {shouldShowInstagram && (
        <div>
          <label className="block text-xs font-medium text-white/90 mb-2">
            Cuenta de Instagram{' '}
            {instagramRequired ? (
              <span className="text-red-400">*</span>
            ) : (
              <span className="text-white/60 text-xs">(opcional)</span>
            )}
          </label>
          <div className="relative">
            <button
              type="button"
              ref={instagramDropdownBtnRef}
              onClick={() => selectedAccountId && instagramAccounts.length && !disabled && handleOpenDropdown('instagram')}
              disabled={!selectedAccountId || !instagramAccounts.length || disabled}
              className={`w-full px-3 py-2 text-left border-2 rounded-lg shadow-sm flex items-center justify-between text-sm backdrop-blur-md transition-all ${
                selectedAccountId && instagramAccounts.length && !disabled
                  ? 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 text-white'
                  : 'bg-white/5 border-white/20 cursor-not-allowed text-white/50'
              }`}
            >
              <div className="flex items-center">
                {selectedInstagramId ? (
                  <div className="font-medium text-white text-sm">{instagramAccounts.find(insta => insta.id === selectedInstagramId)?.username}</div>
                ) : (
                  <span className="text-white/60 text-sm">
                    {!selectedAccountId ? "Selecciona una cuenta primero" : !instagramAccounts.length ? "No hay cuentas disponibles" : "Seleccionar cuenta"}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isInstagramDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isInstagramDropdownOpen && instagramAccounts.length > 0 && createPortal(
              <div
                className="z-[11000] backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-2xl max-h-72 overflow-auto py-2"
                style={{
                  position: 'fixed',
                  top: instagramDropdownPos.top,
                  left: instagramDropdownPos.left,
                  width: instagramDropdownPos.width,
                  minWidth: 240,
                  padding: 0,
                }}
              >
                {instagramAccounts.map((instagram) => (
                  <button
                    key={instagram.id}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      onInstagramSelect?.(instagram);
                      setIsInstagramDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left flex items-center border-b border-white/10 last:border-b-0 hover:bg-white/20 focus:bg-white/20 focus:outline-none transition-all ${instagram.id === selectedInstagramId ? 'bg-blue-500/30 font-semibold' : ''}`}
                  >
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20">
                      <Instagram className="w-4 h-4 text-blue-300" />
                    </div>
                    <div className="font-medium text-white">{instagram.username}</div>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Dropdown */}
      {shouldShowWhatsapp && (
        <div>
          <label className="block text-xs font-medium text-white/90 mb-2">
            Cuenta de WhatsApp{' '}
            {whatsappRequired ? (
              <span className="text-red-400">*</span>
            ) : (
              <span className="text-white/60 text-xs">(opcional)</span>
            )}
          </label>
          <div className="relative">
            <button
              type="button"
              ref={whatsappDropdownBtnRef}
              onClick={() => selectedAccountId && whatsappPhones.length && !disabled && handleOpenDropdown('whatsapp')}
              disabled={!selectedAccountId || !whatsappPhones.length || disabled}
              className={`w-full px-3 py-2 text-left border-2 rounded-lg shadow-sm flex items-center justify-between text-sm backdrop-blur-md transition-all ${
                selectedAccountId && whatsappPhones.length && !disabled
                  ? 'bg-white/10 border-white/30 hover:bg-white/15 hover:border-white/40 text-white'
                  : 'bg-white/5 border-white/20 cursor-not-allowed text-white/50'
              }`}
            >
              <div className="flex items-center">
                {selectedWhatsappPhone ? (
                  <span className="font-medium text-white text-sm">{selectedWhatsappPhone}</span>
                ) : (
                  <span className="text-white/60 text-sm">
                    {!selectedAccountId ? 'Selecciona una cuenta primero' : !whatsappPhones.length ? 'No hay números disponibles' : 'Seleccionar número'}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isWhatsappDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isWhatsappDropdownOpen && whatsappPhones.length > 0 && createPortal(
              <div
                className="z-[11000] backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-2xl max-h-72 overflow-auto py-2"
                style={{
                  position: 'fixed',
                  top: whatsappDropdownPos.top,
                  left: whatsappDropdownPos.left,
                  width: whatsappDropdownPos.width,
                  minWidth: 240,
                  padding: 0,
                }}
              >
                {whatsappPhones.map((phone) => (
                  <button
                    key={phone}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      onWhatsappSelect?.(phone);
                      setIsWhatsappDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left flex items-center border-b border-white/10 last:border-b-0 hover:bg-white/20 focus:bg-white/20 focus:outline-none transition-all ${phone === selectedWhatsappPhone ? 'bg-blue-500/30 font-semibold' : ''}`}
                  >
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20" />
                    <span className="font-medium text-white">{phone}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaDropdowns;
